import { FieldPath, FieldValues, get, useController } from 'react-hook-form';
import {
  FormMultiSelect,
  FormMultiSelectProps,
  MultiSelectOption,
} from '@britishcouncil/react-admin';

import { RHFormFieldProps } from './types';
import { useFormContext } from './FormContext';
import { ReadonlyFormValue } from './ReadonlyFormValue';

type TValue = string | number | boolean;

type RHFormCustomMultiSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = RHFormFieldProps<FormMultiSelectProps<TValue>, TFieldValues, TName> & {
  onChange?: (value: TValue[]) => void;
  readonlyMode?: boolean;
  renderReadonlyValue?: (value: TValue[], options: MultiSelectOption<TValue>[]) => React.ReactNode;
};

export const RHFormCustomMultiSelect = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  onChange,
  readonlyMode,
  placeholder,
  renderReadonlyValue,
  ...props
}: RHFormCustomMultiSelectProps<TFieldValues, TName>) => {
  const { mode } = useFormContext();
  const { field, formState } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const handleOnChange = (value: TValue[]) => {
    field.onChange(value);
    onChange?.(value);
  };

  if (mode === 'read' || readonlyMode) {
    return (
      <ReadonlyFormValue
        label={props.label}
        value={field.value}
        renderValue={(value) => {
          const _value = value as TValue[];
          const options = props.options ?? [];
          const selectedOptions = options.filter((opt) => _value.includes(opt.value));
          return renderReadonlyValue
            ? renderReadonlyValue(_value, selectedOptions)
            : _value.length
              ? selectedOptions.map((opt) => opt.text).join(', ')
              : '-';
        }}
      />
    );
  }

  return (
    <FormMultiSelect
      {...props}
      placeholder={placeholder ?? 'None selected'}
      name={name}
      value={field.value}
      onChange={handleOnChange}
      onBlur={field.onBlur}
      touched
      error={get(formState.errors, name)?.message}
    />
  );
};
